import { RefObject, useEffect, useRef } from "react";
import { observeCard } from "../../utils/events/cardObserver";
import { productCardEvents } from "./productCardEvents";
import { useProductCardEvent } from "./ProductCardEventContext";

type Props = {
  eventsRef: RefObject<HTMLElement | null>;
  inViewRatio?: number;
};

/**
 * Registers a card's root element with the shared IntersectionObserver and
 * dispatches a `card-in-view` event the first time the card is ~100% visible.
 * Renders nothing. Must be used within a ProductCardEventProvider.
 */
export function CardInViewTracker({ eventsRef, inViewRatio }: Props): null {
  const dispatchCardEvent = useProductCardEvent();

  // useProductCardEvent's callback changes when the variant changes; pin it so
  // the effect doesn't re-run and re-observe an already-fired card.
  const dispatchCardEventRef = useRef(dispatchCardEvent);
  dispatchCardEventRef.current = dispatchCardEvent;

  useEffect(
    function observeCardInView() {
      const element = eventsRef.current;
      if (!element) return;
      return observeCard(
        element,
        () => dispatchCardEventRef.current(productCardEvents.cardInView),
        inViewRatio,
      );
    },
    [eventsRef],
  );

  return null;
}
